import React from 'react'
import { Box, Stack, Typography, TextField, Button, Avatar, Divider, FormControl } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Tooltip from '@mui/material/Tooltip'
import { useSelector } from 'react-redux'

import GradeLevelSelector from '../GradeLevelSelector'

import ArchiveClassroomsTable from './ArchiveClassroomsTable'

import { ImageDialogSelector } from '@/components/ImageSelector/ImageDialogSelector'
import SubjectSelector from '@/components/SubjectSelector'
import MultipleLanguagesSelector from '@/components/MultipleLanguageSelector'
import LanguageSelector from '@/components/LanguageSelector'
import SchoolsModal from '@/components/SchoolsAutoComplete/SchoolsModal'
import AccountDeleteConfirmationModal from '@/components/AccountDeleteConfirmationModal'

export default function GeneralTabContent({ hook }) {
  const profile = useSelector(({ user: { profile } }) => profile)
  const isTeacher = profile?.isTeacher || false
  const {
    // State
    avatar,
    displayName,
    language,
    nativeLanguage,
    subjects,
    nativeLanguages,
    defaultPoints,
    defaultRepeat,
    schoolName,
    schoolSearchOpen,
    deleteModalOpen,
    openDialog,
    archivedSections,
    selectedClassrooms,
    gradeLevel,

    // Setters
    setSubjects,
    setNativeLanguages,
    setSchoolSearchOpen,
    setDeleteModalOpen,

    // Handlers
    handleProfileChange,
    handleClassroomSelect,
    handleArchiveToggle,
    handleDeleteClassroom,
    handleDeleteAvatar,
    handleAvatarUpdate,
    handleSchoolSelect,
    handleClose,
    handleOpen,
  } = hook

  return (
    <Stack bgcolor="#fff" gap="20px">
      {/* Header */}
      <Stack gap="4px">
        <Typography color="#101828" fontSize={18} fontWeight={600} lineHeight="28px">
          General Settings
        </Typography>
        <Typography color="#475467" fontSize={14} lineHeight="20px">
          Update your photo and personal details here.
        </Typography>
      </Stack>
      <Divider sx={{ borderColor: '#EAECF0' }} />
      {/* Profile Photo */}
      <Stack alignItems="center" direction="row" gap={3}>
        <Stack>
          <Typography fontSize={14} fontWeight={600} lineHeight="20px">
            Your photo
          </Typography>
          <Typography color="#475467" fontSize={14} lineHeight="20px">
            This will be displayed on your profile.
          </Typography>
        </Stack>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Tooltip arrow title="Update Avatar">
            <Avatar src={avatar} sx={{ width: 56, height: 56 }} />
          </Tooltip>
        </Box>
        <ImageDialogSelector handleClose={handleClose} open={openDialog} onClickImage={handleAvatarUpdate} />
        {/* Add update/delete avatar logic as needed */}
        <Stack alignItems="center" direction="row" gap={2}>
          <Button color="error" sx={{ textTransform: 'none', fontWeight: 500 }} onClick={handleDeleteAvatar}>
            Delete
          </Button>
          <Button color="secondary" sx={{ textTransform: 'none', fontWeight: 500 }} onClick={handleOpen}>
            Update
          </Button>
        </Stack>
      </Stack>
      <Divider sx={{ borderColor: '#EAECF0' }} />
      {/* Profile Details */}
      <Stack gap="16px">
        <Typography fontSize={14} fontWeight={600} lineHeight="20px">
          Profile details
        </Typography>
        <Stack gap="6px" width="100%">
          <Typography fontSize={14} fontWeight={500} lineHeight="20px">
            Name
          </Typography>
          <TextField
            placeholder="Display Name"
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                height: 44,
                background: '#FFF',
                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px !important' },
              },
              width: '100%',
            }}
            value={displayName}
            onChange={e => handleProfileChange('displayName', e.target.value)}
          />
        </Stack>
        <Stack direction="row" gap="24px">
          <Stack gap="6px" width="100%">
            <Typography fontSize={14} fontWeight={500} lineHeight="20px">
              {isTeacher ? 'Teaching Language' : 'Learning Language'}
            </Typography>
            <LanguageSelector language={language} setLanguage={l => handleProfileChange('language', l)} />
          </Stack>
          <Stack gap="6px" width="100%">
            <Typography fontSize={14} fontWeight={500} lineHeight="20px">
              Native Language
            </Typography>
            <LanguageSelector language={nativeLanguage} setLanguage={l => handleProfileChange('nativeLanguage', l)} />
          </Stack>
        </Stack>
      </Stack>
      <Divider sx={{ borderColor: '#EAECF0' }} />
      {/* Teachers Settings */}
      {isTeacher ? (
        <>
          <Stack gap="16px">
            <Typography fontSize={14} fontWeight={600} lineHeight="20px">
              Teachers Settings
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={3}>
              <FormControl sx={{ flex: 1 }}>
                <Typography fontSize={14} fontWeight={500} lineHeight="20px" mb={0.5}>
                  Subjects
                </Typography>
                <SubjectSelector multiple setSubjects={setSubjects} subjects={subjects} />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <Typography fontSize={14} fontWeight={500} lineHeight="20px" mb={0.5}>
                  Student Native Languages
                </Typography>
                <MultipleLanguagesSelector
                  multiple
                  selectedLanguages={nativeLanguages}
                  setSelectedLanguages={setNativeLanguages}
                />
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
              <Stack gap="4px" sx={{ flex: 1 }}>
                <Typography fontSize={14} fontWeight={500} lineHeight="20px" mb={0.5}>
                  Default Assignment Points
                </Typography>
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 44,
                      background: '#FFF',
                      '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px !important' },
                    },
                    width: '100%',
                  }}
                  type="number"
                  value={defaultPoints}
                  onChange={e => handleProfileChange('defaultPoints', e.target.value)}
                />
              </Stack>
              <Stack gap="4px" sx={{ flex: 1 }}>
                <Typography fontSize={14} fontWeight={500} lineHeight="20px" mb={0.5}>
                  Default Phrase Repeat
                </Typography>
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 44,
                      background: '#FFF',
                      '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px !important' },
                    },
                    width: '100%',
                  }}
                  type="number"
                  value={defaultRepeat}
                  onChange={e => handleProfileChange('defaultRepeat', e.target.value)}
                />
              </Stack>
            </Stack>
            <Stack gap="4px">
              <Typography fontSize={14} fontWeight={500} lineHeight="20px">
                School
              </Typography>
              <TextField
                readOnly
                sx={{
                  '& .MuiInputBase-root': {
                    height: 44,
                    background: '#FFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px !important' },
                  },
                  width: '100%',
                }}
                value={schoolName}
                onClick={() => setSchoolSearchOpen(true)}
              />
            </Stack>
            <SchoolsModal
              open={schoolSearchOpen}
              onClose={() => setSchoolSearchOpen(false)}
              onSelect={handleSchoolSelect}
            />
          </Stack>
          <Divider sx={{ borderColor: '#EAECF0' }} />
          {/* Archive Classrooms */}
          <Box>
            <Typography fontSize={15} fontWeight={600} mb={2}>
              Archive Classrooms
            </Typography>
            <ArchiveClassroomsTable
              sections={archivedSections}
              selectedClassrooms={selectedClassrooms}
              onArchiveToggle={handleArchiveToggle}
              onDeleteClassroom={handleDeleteClassroom}
              onSelectClassroom={handleClassroomSelect}
            />
          </Box>
          <Divider sx={{ borderColor: '#EAECF0' }} />
        </>
      ) : null}
      {!isTeacher && (
        <>
          <Stack gap="4px">
            <Typography fontSize={14} fontWeight={600} lineHeight="20px">
              Grade Level
            </Typography>
            <GradeLevelSelector gradeLevel={gradeLevel} setGradeLevel={l => handleProfileChange('gradeLevel', l)} />
          </Stack>
          <Divider sx={{ borderColor: '#EAECF0' }} />
        </>
      )}
      {/* Danger Zone */}
      <Box>
        <Typography color="#344054" fontSize={14} fontWeight={600} lineHeight="20px" mb={1}>
          Danger Zone
        </Typography>
        <Button
          startIcon={<DeleteOutlineIcon />}
          sx={{ color: '#B42318', textTransform: 'none', fontWeight: 500 }}
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete Account
        </Button>
        <AccountDeleteConfirmationModal handleClose={() => setDeleteModalOpen(false)} open={deleteModalOpen} />
      </Box>
    </Stack>
  )
}
